import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

// ─────────────────────────────────────────────────────────────────────────────
// KONSEP 1: KINETIC BAUHAUS CALENDAR (MODERN POSTER IN MOTION)
// Vibe: Graphic Design Grid, Architectural Type, Monochromatic High-Contrast + Brand Accent
// ─────────────────────────────────────────────────────────────────────────────
export const KyuStoreBauhausCalendar: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const animHeader = spring({
    frame,
    fps,
    config: { damping: 16, stiffness: 140 },
  });

  const animGrid1 = spring({
    frame: frame - 10,
    fps,
    config: { damping: 15, stiffness: 150 },
  });

  const animGrid2 = spring({
    frame: frame - 20,
    fps,
    config: { damping: 15, stiffness: 150 },
  });

  const animTicker = spring({
    frame: frame - 25,
    fps,
    config: { damping: 14, stiffness: 180 },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0a0c0b",
        color: "#ffffff",
        fontFamily: "'Syne', 'Inter', -apple-system, sans-serif",
        padding: "60px 48px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxSizing: "border-box",
      }}
    >
      {/* Precision Geometric Grid Lines */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderLeft: "1px solid rgba(255,255,255,0.08)",
          borderRight: "1px solid rgba(255,255,255,0.08)",
          margin: "0 48px",
          pointerEvents: "none",
        }}
      />

      {/* TOP HEADER / PUBLIC ANNOUNCEMENT */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          transform: `translateY(${interpolate(animHeader, [0, 1], [-30, 0])}px)`,
          opacity: animHeader,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "2px solid #ffffff",
            paddingBottom: "20px",
          }}
        >
          <div
            style={{
              fontSize: "20px",
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#e8f36e",
            }}
          >
            [ OFFICIAL ROUTINE BULLETIN ]
          </div>
          <div
            style={{
              fontFamily: "monospace",
              fontSize: "18px",
              color: "#889990",
            }}
          >
            KYUSTORE.COM // ARCHIVE
          </div>
        </div>

        {/* Big Bold Headline */}
        <div style={{ marginTop: "32px" }}>
          <div
            style={{
              fontSize: "36px",
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: "#889990",
              textTransform: "uppercase",
            }}
          >
            JADWAL RESMI
          </div>
          <div
            style={{
              fontSize: "110px",
              fontWeight: 900,
              lineHeight: 0.9,
              letterSpacing: "-0.05em",
              color: "#ffffff",
              textTransform: "uppercase",
              marginTop: "8px",
            }}
          >
            FLASH SALE
          </div>
          <div
            style={{
              fontSize: "26px",
              fontWeight: 500,
              color: "#e8f36e",
              marginTop: "16px",
              letterSpacing: "-0.01em",
            }}
          >
            Dua periode rutin setiap bulan. Pasang alarm sekarang.
          </div>
        </div>
      </div>

      {/* CENTER: ARCHITECTURAL DUAL DATE BLOCKS (PERTENGAHAN & AKHIR) */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        {/* Block 1: Pertengahan Bulan */}
        <div
          style={{
            backgroundColor: "#111614",
            border: "1.5px solid rgba(255,255,255,0.15)",
            borderLeft: "8px solid #e8f36e",
            padding: "36px 40px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            transform: `scale(${animGrid1})`,
          }}
        >
          <div>
            <div
              style={{
                fontSize: "18px",
                fontFamily: "monospace",
                color: "#e8f36e",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: "8px",
              }}
            >
              PERIODE 01 // DROP SATU
            </div>
            <div
              style={{
                fontSize: "44px",
                fontWeight: 800,
                color: "#ffffff",
                letterSpacing: "-0.03em",
              }}
            >
              PERTENGAHAN BULAN
            </div>
            <div
              style={{
                fontSize: "22px",
                color: "#889990",
                marginTop: "6px",
              }}
            >
              Tepat di pertengahan bulan kalender
            </div>
          </div>

          <div
            style={{
              textAlign: "right",
              borderLeft: "1px solid rgba(255,255,255,0.1)",
              paddingLeft: "32px",
            }}
          >
            <div
              style={{
                fontSize: "76px",
                fontWeight: 900,
                lineHeight: 1,
                color: "#e8f36e",
                fontFamily: "monospace",
              }}
            >
              15
            </div>
            <div style={{ fontSize: "16px", color: "#889990", textTransform: "uppercase" }}>
              MID MONTH
            </div>
          </div>
        </div>

        {/* Block 2: Akhir Bulan */}
        <div
          style={{
            backgroundColor: "#111614",
            border: "1.5px solid rgba(255,255,255,0.15)",
            borderLeft: "8px solid #ffffff",
            padding: "36px 40px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            transform: `scale(${animGrid2})`,
          }}
        >
          <div>
            <div
              style={{
                fontSize: "18px",
                fontFamily: "monospace",
                color: "#ffffff",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: "8px",
              }}
            >
              PERIODE 02 // DROP DUA
            </div>
            <div
              style={{
                fontSize: "44px",
                fontWeight: 800,
                color: "#ffffff",
                letterSpacing: "-0.03em",
              }}
            >
              AKHIR BULAN
            </div>
            <div
              style={{
                fontSize: "22px",
                color: "#889990",
                marginTop: "6px",
              }}
            >
              Momen gajian & penutupan bulan
            </div>
          </div>

          <div
            style={{
              textAlign: "right",
              borderLeft: "1px solid rgba(255,255,255,0.1)",
              paddingLeft: "32px",
            }}
          >
            <div
              style={{
                fontSize: "76px",
                fontWeight: 900,
                lineHeight: 1,
                color: "#ffffff",
                fontFamily: "monospace",
              }}
            >
              PAY
            </div>
            <div style={{ fontSize: "16px", color: "#889990", textTransform: "uppercase" }}>
              DAY EVENT
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM TICKER & CALL TO ACTION (SAFE ZONE: >220PX FROM BOTTOM) */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          marginBottom: "120px", // safe margin above TikTok UI
          transform: `translateY(${interpolate(animTicker, [0, 1], [30, 0])}px)`,
          opacity: animTicker,
        }}
      >
        <div
          style={{
            backgroundColor: "#e8f36e",
            color: "#0a0c0b",
            padding: "24px 32px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 800,
          }}
        >
          <div style={{ fontSize: "24px", letterSpacing: "-0.01em" }}>
            JANGAN ASAL CHECKOUT. TUNGGU TANGGAL MAINNYA.
          </div>
          <div
            style={{
              fontSize: "20px",
              fontFamily: "monospace",
              backgroundColor: "#0a0c0b",
              color: "#e8f36e",
              padding: "8px 16px",
            }}
          >
            KYUSTORE.COM ➔
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// KONSEP 2: MODERN MONOLITHIC BENTO (DIGITAL METRIC & MINIMAL TECH)
// Vibe: Clean Bento Hierarchy, Matte Obsidian, Electric Accents, Precision Data
// ─────────────────────────────────────────────────────────────────────────────
export const KyuStoreMonolithBento: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const enterMain = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 120 },
  });

  const enterBento1 = spring({
    frame: frame - 12,
    fps,
    config: { damping: 16, stiffness: 140 },
  });

  const enterBento2 = spring({
    frame: frame - 20,
    fps,
    config: { damping: 16, stiffness: 140 },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#070908",
        color: "#ffffff",
        fontFamily: "'Inter', -apple-system, sans-serif",
        padding: "64px 48px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxSizing: "border-box",
      }}
    >
      {/* Subtle Radial Glow */}
      <div
        style={{
          position: "absolute",
          top: "15%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "700px",
          height: "700px",
          background:
            "radial-gradient(circle, rgba(232,243,110,0.08) 0%, rgba(7,9,8,0) 70%)",
          pointerEvents: "none",
        }}
      />

      {/* TOP: BRAND PILL & CLEAN HERO HEADLINE */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          transform: `translateY(${interpolate(enterMain, [0, 1], [-20, 0])}px)`,
          opacity: enterMain,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "12px",
            backgroundColor: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            padding: "10px 22px",
            borderRadius: "100px",
            marginBottom: "28px",
          }}
        >
          <div
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: "#e8f36e",
              boxShadow: "0 0 12px #e8f36e",
            }}
          />
          <div
            style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#d0dbd4",
              letterSpacing: "0.05em",
            }}
          >
            KYU STORE • SMART TOP UP ROUTINE
          </div>
        </div>

        <div
          style={{
            fontSize: "84px",
            fontWeight: 800,
            lineHeight: 1.0,
            letterSpacing: "-0.04em",
            color: "#ffffff",
          }}
        >
          Top Up Cerdas,
        </div>
        <div
          style={{
            fontSize: "84px",
            fontWeight: 800,
            lineHeight: 1.0,
            letterSpacing: "-0.04em",
            color: "#e8f36e",
          }}
        >
          Tunggu Tanggalnya.
        </div>
        <div
          style={{
            fontSize: "26px",
            color: "#7e9188",
            marginTop: "16px",
            lineHeight: 1.4,
            maxWidth: "85%",
          }}
        >
          Flash Sale berkala tanpa ribet. Tersedia eksklusif di 2 momen setiap bulannya.
        </div>
      </div>

      {/* CENTER: BENTO CARDS */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "24px",
        }}
      >
        {/* Bento Card 1: Mid Month */}
        <div
          style={{
            backgroundColor: "rgba(20, 26, 23, 0.7)",
            border: "1px solid rgba(232,243,110, 0.3)",
            borderRadius: "24px",
            padding: "36px 36px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            transform: `translateY(${interpolate(enterBento1, [0, 1], [30, 0])}px)`,
            opacity: enterBento1,
            backdropFilter: "blur(20px)",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: 700,
                color: "#e8f36e",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: "8px",
              }}
            >
              JADWAL #1
            </div>
            <div
              style={{
                fontSize: "42px",
                fontWeight: 800,
                color: "#ffffff",
                letterSpacing: "-0.02em",
              }}
            >
              Pertengahan Bulan
            </div>
            <div
              style={{
                fontSize: "22px",
                color: "#97a89f",
                marginTop: "6px",
              }}
            >
              Refill kuota mingguan / pass game
            </div>
          </div>

          <div
            style={{
              backgroundColor: "#e8f36e",
              color: "#070908",
              fontWeight: 800,
              fontSize: "22px",
              padding: "16px 28px",
              borderRadius: "16px",
            }}
          >
            TGL 15
          </div>
        </div>

        {/* Bento Card 2: Payday / Akhir Bulan */}
        <div
          style={{
            backgroundColor: "rgba(20, 26, 23, 0.7)",
            border: "1px solid rgba(255,255,255, 0.12)",
            borderRadius: "24px",
            padding: "36px 36px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            transform: `translateY(${interpolate(enterBento2, [0, 1], [30, 0])}px)`,
            opacity: enterBento2,
            backdropFilter: "blur(20px)",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: 700,
                color: "#ffffff",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: "8px",
              }}
            >
              JADWAL #2
            </div>
            <div
              style={{
                fontSize: "42px",
                fontWeight: 800,
                color: "#ffffff",
                letterSpacing: "-0.02em",
              }}
            >
              Akhir Bulan (Payday)
            </div>
            <div
              style={{
                fontSize: "22px",
                color: "#97a89f",
                marginTop: "6px",
              }}
            >
              Spesial penutupan bulan & gajian
            </div>
          </div>

          <div
            style={{
              backgroundColor: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.2)",
              color: "#ffffff",
              fontWeight: 800,
              fontSize: "22px",
              padding: "16px 28px",
              borderRadius: "16px",
            }}
          >
            PAYDAY
          </div>
        </div>
      </div>

      {/* BOTTOM ACTION BAR (SAFE ZONE: >220PX FROM BOTTOM) */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          marginBottom: "120px", // safe margin above TikTok UI
          borderTop: "1px solid rgba(255,255,255,0.1)",
          paddingTop: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: "22px", color: "#7e9188", fontWeight: 500 }}>
          Simpan jadwalnya. Cek langsung di web resmi.
        </div>
        <div
          style={{
            fontSize: "24px",
            fontWeight: 800,
            color: "#e8f36e",
          }}
        >
          kyustore.com ➔
        </div>
      </div>
    </AbsoluteFill>
  );
};
